import { rolesMatch } from 'utils/user';
import { HOST, ORG_ID, BASE_API_URL } from './config';
import { Role } from './constants/role';

export const authTokenKey = 'yb-auth-token';
export const userDataKey = 'yb-user-data';
export const getAuthToken = () => localStorage.getItem(authTokenKey);
export const getUserData = () => {
  const userData = localStorage.getItem(userDataKey);
  return userData ? JSON.parse(userData) : null;
};

async function fetchUserData() {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }
  const user = parseToken();
  const request = new Request(`${HOST}/c/org/${user.orgId}/users/me?_attributes=id,email,information`, {
    method: 'GET',
    headers: new Headers({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    }),
  });

  const response = await fetch(request);
  if (response.status < 200 || response.status >= 300) {
    throw new Error(`Failed to fetch user data: ${response.statusText}`);
  }

  const userData = await response.json();
  localStorage.setItem(userDataKey, JSON.stringify(userData.data));
  return userData;
}

export default {
  login: (params) => {
    if (params.type === 'google') {
      return loginWithGoogle(params);
    }

    // Default email/password login
    const { username, password } = params;
    const request = new Request(`${HOST}/${(ORG_ID ? `org/${ORG_ID}/` : '')}auth/login/email`, {
      method: 'POST',
      body: JSON.stringify({ email: username, password }),
      headers: new Headers({ 'Content-Type': 'application/json' }),
    });
    return fetch(request)
      .then((response) => {
        if (response.status < 200 || response.status >= 300) {
          throw new Error(response.statusText);
        }
        return response.json();
      })
      .then(({ data: { token } }) => {
        localStorage.setItem(authTokenKey, token);
        // Fetch user data after successful login
        return fetchUserData();
      });
  },
  checkError: (error) => {
    if (!error) {
      return Promise.reject(new Error('Unknown Authorized'));
    } if (error?.status === 401) {
      localStorage.removeItem(authTokenKey);
      return Promise.reject(new Error('Not Authorized'));
    }
    return Promise.resolve();
  },
  checkAuth: () => (isLoggedIn() ? Promise.resolve() : Promise.reject()),
  logout: () => {
    localStorage.removeItem(authTokenKey);
    localStorage.removeItem(userDataKey);
    return Promise.resolve();
  },
  getIdentity: () => {
    const userData = getUserData();
    return userData ? Promise.resolve(userData) : Promise.reject();
  },
  getPermissions: () => {
    const user = parseToken();
    return Promise.resolve(user.roles);
  },

  isLoggedIn,
};
function parseToken() {
  const token = getAuthToken();
  if (!token) {
    return Promise.reject();
  }
  return JSON.parse(decodeURIComponent(atob(token.split('.')[1])));
}

function loginWithGoogle({ tokenId }) {
  const request = new Request(`${HOST}/${(ORG_ID ? `org/${ORG_ID}/` : '')}auth/oauth2/login`, {
    method: 'POST',
    body: JSON.stringify({ tokenId }),
    headers: new Headers({ 'Content-Type': 'application/json' }),
  });
  return fetch(request)
    .then((response) => {
      if (response.status < 200 || response.status >= 300) {
        throw new Error(response.statusText);
      }
      return response.json();
    })
    .then(({ data: { token } }) => {
      localStorage.setItem(authTokenKey, token);
      // Fetch user data after successful Google login
      return fetchUserData();
    });
}

function isLoggedIn() {
  const token = localStorage.getItem(authTokenKey);
  return token && isAdmin(token);
}

function isAdmin(token) {
  try {
    const user = JSON.parse(decodeURIComponent(atob(token.split('.')[1])));


    return user.roles && rolesMatch({
      roles: user.roles,
      role: ORG_ID ? Role.STAFF : Role.SUPER_ADMIN,
    });
  } catch (e) {
    return false;
  }
}
